import type ReactGridLayout from 'react-grid-layout';
import { Layout } from 'react-grid-layout';

/* ============================================================================
 * DASHBOARD LAYOUT TYPE DEFINITIONS
 * All type definitions and interfaces for dashboard layout system
 * ============================================================================ */

// Core layout structure for responsive breakpoints
export interface DashboardLayouts extends ReactGridLayout.Layouts {
  lg: Layout[];
  md: Layout[];
  sm: Layout[];
  xs: Layout[];
  xxs: Layout[];
}

/** Preserve only the dashboard's persisted breakpoint set when accepting grid callbacks. */
export const toDashboardLayouts = (layouts: ReactGridLayout.Layouts): DashboardLayouts => ({
  lg: [...(layouts.lg || [])],
  md: [...(layouts.md || [])],
  sm: [...(layouts.sm || [])],
  xs: [...(layouts.xs || [])],
  xxs: [...(layouts.xxs || [])],
});

// Named layout with metadata
export interface NamedLayout {
  id: string;
  name: string;
  layouts: DashboardLayouts;
  widgets?: Widget[];  // Widget data including visibility states
  isDefault?: boolean;
  createdAt?: string;
  lastModified?: string;
}

// View mode layouts mapping
export interface ViewModeLayouts {
  [viewMode: string]: DashboardLayouts;
}

// View mode widget data mapping
export interface ViewModeWidgetData {
  [viewMode: string]: Widget[];
}

// Layouts with widget data
export interface LayoutsWithWidgetData {
  layouts: ViewModeLayouts;
  widgetData?: ViewModeWidgetData;
}

// View mode named layouts mapping
export interface ViewModeNamedLayouts {
  [viewMode: string]: NamedLayout[];
}

// API response types
export interface LayoutResponse {
  layouts: ViewModeLayouts;
  lastModified: { [viewMode: string]: string };
}

export interface SaveLayoutRequest {
  layouts: DashboardLayouts;
}

export interface SaveLayoutResponse {
  success: boolean;
  viewMode: string;
  lastModified: string;
}

// Cache interface for offline support
export interface LayoutCache {
  layouts: ViewModeLayouts;
  namedLayouts: ViewModeNamedLayouts;
  lastModified: { [viewMode: string]: string };
  cachedAt: number;
  pendingChanges: { [viewMode: string]: DashboardLayouts };
}

// Service configuration
export interface ServiceConfig {
  cacheTimeout: number; // ms
  retryAttempts: number;
  retryDelay: number; // ms
}

// Widget types for backend communication
export interface WidgetPosition {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WidgetConfig {
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
  [key: string]: string | number | boolean | undefined;
}

export interface Widget {
  id: string;
  type: string;
  position: WidgetPosition;
  config: WidgetConfig;
  is_visible: boolean;
  /** Legacy fields accepted from persisted dashboard JSON. */
  isVisible?: boolean;
  defaultSize?: { w: number; h: number };
  i?: string;
  x?: number;
  y?: number;
  w?: number;
  h?: number;
  minW?: number;
  maxW?: number;
  minH?: number;
  maxH?: number;
  static?: boolean;
  isDraggable?: boolean;
  isResizable?: boolean;
}

const isWidgetPosition = (value: unknown): value is WidgetPosition =>
  value !== null &&
  typeof value === 'object' &&
  ['x', 'y', 'w', 'h'].every((field) => typeof Reflect.get(value, field) === 'number');

const isWidgetConfig = (value: unknown): value is WidgetConfig =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/** Normalize the documented flat legacy widget wire shape without losing widget data. */
export const normalizeWidgetArray = (value: unknown): Widget[] | null => {
  if (!Array.isArray(value)) return null;
  const widgets = value.map((widget): Widget | null => {
    if (widget === null || typeof widget !== 'object') return null;
    const id = Reflect.get(widget, 'id') ?? Reflect.get(widget, 'i');
    const type = Reflect.get(widget, 'type');
    const positionValue = Reflect.get(widget, 'position');
    const position = isWidgetPosition(positionValue)
      ? positionValue
      : {
          x: Reflect.get(widget, 'x'), y: Reflect.get(widget, 'y'),
          w: Reflect.get(widget, 'w'), h: Reflect.get(widget, 'h'),
        };
    const config = Reflect.get(widget, 'config');
    const visible = Reflect.get(widget, 'is_visible') ?? Reflect.get(widget, 'isVisible');
    return typeof id === 'string' && typeof type === 'string' && isWidgetPosition(position) &&
      isWidgetConfig(config) && typeof visible === 'boolean'
      ? { id, type, position, config, is_visible: visible }
      : null;
  });
  return widgets.every((widget): widget is Widget => widget !== null) ? widgets : null;
};

export interface GridConfig {
  breakpoints?: DashboardLayouts;
  [key: string]: DashboardLayouts | undefined;
}

// Local storage types
export interface LocalStorageLayoutData {
  layouts: DashboardLayouts;
  lastModified: string;
  syncStatus: 'synced' | 'pending';
}
